import { useQuery, QueryClient } from '@tanstack/react-query';
import { TDR } from 'tdr-common';
import { useDebounce } from 'use-debounce';
import config from '../../common/config';
import {
	toTableDTO,
	ClientCalendarAvailabilityInput,
	ClientSupportedGroupSizesInput
} from './dto';
import Availability = TDR.Availability;

// Shared cache settings for calendar availability queries
const CALENDAR_CACHE_SETTINGS = {
	staleTime: 2 * 60 * 1000, // 2 minutes
	gcTime: 5 * 60 * 1000 // 5 minutes
};

export const useSupportedGroupSizes = (input: ClientSupportedGroupSizesInput) => {
	const { tables } = input;

	const query = useQuery({
		enabled: !!tables,
		queryKey: ['supportedGroupSizes', tables],
		queryFn: () => getSupportedGroupSizes({ tables: toTableDTO(tables) })
	});

	return {
		supportedGroupSizes: query.data?.supportedGroupSizes,
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
		refetch: query.refetch,
		isFetched: query.isFetched
	};
};

async function getSupportedGroupSizes(input: Availability.getSupportedGroupSizesInput): Promise<Availability.getSupportedTableSizeOutput> {
	const response = await fetch(`${config.apiHost}/getSupportedGroupSizes`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(input)
	});

	const responsePayload = await response.json() as Availability.getSupportedTableSizeOutput;
	if (!response.ok) {
		throw response;
	}
	else if (!responsePayload?.success) {
		throw Error('Could Not Supported Group Sizes');
	}

	return responsePayload;
}

export function useCalendarAvailability(
	input: ClientCalendarAvailabilityInput,
	isReleasing = false,
	disabled = false
) {
	const { tables, month, restaurantId, ignoreAvailability, time, reservationId } = input;
	const debouncedMonth = useDebounce(month, 300);
	const query = useQuery({
		enabled:
			!isReleasing
			&& !disabled
			&& Array.isArray(tables)
			&& tables.length > 0
			&& !!restaurantId
			&& !!debouncedMonth[0],
		queryKey: ['calendarAvailability', tables, debouncedMonth[0], restaurantId, ignoreAvailability, time, reservationId],
		queryFn: () => getCalendarAvailability({
			month: debouncedMonth[0],
			tables: toTableDTO(tables),
			restaurantId,
			ignoreAvailability,
			time,
			reservationId
		}),
		...CALENDAR_CACHE_SETTINGS
	});

	return query;
}

/**
 * Prefetch calendar availability data
 * Use this to proactively load calendar data in the background
 */
export function prefetchCalendarAvailability(
	queryClient: QueryClient,
	input: ClientCalendarAvailabilityInput
): Promise<Availability.getCalendarAvailabilityOutput> {
	const { tables, month, restaurantId, ignoreAvailability, time, reservationId } = input;

	return queryClient.fetchQuery({
		queryKey: ['calendarAvailability', tables, month, restaurantId, ignoreAvailability, time, reservationId],
		queryFn: () => getCalendarAvailability({
			month,
			tables: toTableDTO(tables),
			restaurantId,
			ignoreAvailability,
			time,
			reservationId
		}),
		...CALENDAR_CACHE_SETTINGS
	});
}

export async function getCalendarAvailability(input: Availability.getCalendarAvailabilityInput): Promise<Availability.getCalendarAvailabilityOutput> {
	const response = await fetch(`${config.apiHost}/getCalendarAvailability`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(input)
	});

	const responsePayload = await response.json() as Availability.getCalendarAvailabilityOutput;

	if (!response.ok) {
		throw response;
	}
	else if (!responsePayload?.success) {
		throw Error('Could Not Get Monthly Availability');
	}

	return responsePayload;
}

